import moment from "moment-timezone";
import { base64Encode } from "../utils/base64";
import { buildContractReportQuery } from "./contractReportQuery";
import type { ArgAdapter, ContextCheck, ContextInjection, ToolContext, ToolResult } from "./tools";

// listContractReports is the one endpoint whose `q` is a base64-encoded JSON query that must carry
// the org id — the model can't produce that, so the executor builds it from the real filter params
// the catalog advertises (see contractReportQuery.ts).
export const contractArgAdapters: Record<string, ArgAdapter> = {
  listContractReports: (args, ctx) => ({
    q: base64Encode(JSON.stringify(buildContractReportQuery(args, ctx.auth.organizationId))),
  }),
};

const normalize = (value: string) => value.trim().toLowerCase();

const matchContractDepartment = (ctx: ToolContext, name: unknown) => {
  const departments = ctx.auth.contractDepartments ?? [];
  if (typeof name === "string" && name.trim()) {
    return departments.find((d) => normalize(d.name) === normalize(name));
  }
  return departments.length === 1 ? departments[0] : undefined;
};

const namedDepartment = (args: Record<string, unknown>): unknown => {
  const direct = args["homeDepartment"];
  if (direct !== undefined) return direct;
  const nested = args["createContractRequest"];
  return nested && typeof nested === "object" ? (nested as Record<string, unknown>)["homeDepartment"] : undefined;
};

const refuse = (deviationCode: string, message: string): ToolResult => ({ ok: false, deviationCode, message });

export const contractContextChecks: Record<string, ContextCheck> = {
  createContract: (args, ctx) => {
    const departments = ctx.auth.contractDepartments ?? [];
    if (departments.length === 0) return null;
    if (matchContractDepartment(ctx, namedDepartment(args))) return null;
    const named = namedDepartment(args);
    const names = departments.map((d) => d.name).join(", ");
    return refuse(
      "UNKNOWN_DEPARTMENT",
      typeof named === "string" && named.trim()
        ? `Fant ingen avdeling som heter «${named}» du kan opprette kontrakter i. Velg blant: ${names}.`
        : `Spør brukeren hvilken avdeling kontrakten skal ligge under. Velg blant: ${names}.`,
    );
  },
};

// createContract's depId and timezone are client context, not model input, so the catalog advertises
// only the title and the department NAME.
export const contractContextInjections: Record<string, ContextInjection> = {
  createContract: {
    bodyKey: "createContractRequest",
    pathParams: {
      depId: (ctx, args) => matchContractDepartment(ctx, namedDepartment(args))?.id ?? ctx.auth.departmentId,
    },
    bodyDefaults: { timezone: () => moment.tz.guess() },
    excludeFromBody: ["homeDepartment"],
  },
  createOrganizationEntry: {
    bodyKey: "createOrganizationEntryRequest",
    bodyDefaults: { industryIds: () => [], countryId: (ctx) => ctx.defaultCountryId },
  },
};
